import { useQuery } from '@tanstack/react-query'
import { useParams, Link } from 'react-router-dom'
import { Calendar, User, Tag, ArrowLeft, Share2 } from 'lucide-react'
import SEOHead from '@/components/SEOHead'
import api from '@/services/api'

interface Post {
  id: number
  slug: string
  title: string
  excerpt: string | null
  content: string
  meta_title: string | null
  meta_description: string | null
  meta_keywords: string | null
  og_image: string | null
  canonical_url: string | null
  schema_type: string | null
  category: string | null
  tags: string | null
  published_at: string | null
  author_name: string | null
  related_slugs: string | null
  internal_links: string | null
}

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>()

  const { data: post, isLoading, isError } = useQuery<Post>({
    queryKey: ['blog-post', slug],
    queryFn: async () => {
      const res = await api.get(`/blog/${slug}`)
      return res.data
    },
    enabled: !!slug,
  })

  const { data: relatedData } = useQuery({
    queryKey: ['blog-related', post?.category],
    queryFn: async () => {
      const res = await api.get('/blog', {
        params: { category: post?.category, per_page: 3 },
      })
      return res.data
    },
    enabled: !!post?.category,
  })

  if (isLoading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20">
        <div className="space-y-4 animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-3/4" />
          <div className="h-4 bg-gray-200 rounded w-1/2" />
          <div className="h-64 bg-gray-200 rounded" />
        </div>
      </div>
    )
  }

  if (isError || !post) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Article Not Found</h1>
        <p className="text-gray-600 mb-6">This article may have moved or no longer exists.</p>
        <Link to="/blog" className="text-primary-600 font-semibold hover:underline">
          ← Back to Blog
        </Link>
      </div>
    )
  }

  const keywords = post.meta_keywords ? post.meta_keywords.split(',').map(k => k.trim()) : []
  const tags = post.tags ? post.tags.split(',').map(t => t.trim()) : []
  const canonicalUrl = post.canonical_url || `https://www.siluxmobile.co.uk/blog/${post.slug}`

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': post.schema_type || 'Article',
    headline: post.meta_title || post.title,
    description: post.meta_description || post.excerpt || '',
    image: post.og_image || 'https://www.siluxmobile.co.uk/og-image.png',
    author: { '@type': 'Organization', name: post.author_name || 'Silux Mobile' },
    publisher: {
      '@type': 'Organization',
      name: 'Silux Mobile',
      logo: { '@type': 'ImageObject', url: 'https://www.siluxmobile.co.uk/logo.png' },
    },
    datePublished: post.published_at,
    dateModified: post.published_at,
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
  }

  const breadcrumbs = [
    { name: 'Home', url: 'https://www.siluxmobile.co.uk/' },
    { name: 'Blog', url: 'https://www.siluxmobile.co.uk/blog' },
    { name: post.title, url: canonicalUrl },
  ]

  const relatedPosts = (relatedData?.posts || []).filter((p: any) => p.slug !== slug).slice(0, 3)

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title: post.title, url: window.location.href })
    } else {
      navigator.clipboard.writeText(window.location.href)
    }
  }

  return (
    <>
      <SEOHead
        title={post.meta_title || `${post.title} | Silux Mobile`}
        description={post.meta_description || post.excerpt || ''}
        keywords={keywords}
        ogImage={post.og_image || undefined}
        canonical={canonicalUrl}
        breadcrumbs={breadcrumbs}
      />
      {/* Inject Article schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />

      <article className="max-w-3xl mx-auto px-4 py-12">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-gray-500 mb-8">
          <Link to="/" className="hover:text-primary-600">Home</Link>
          <span>/</span>
          <Link to="/blog" className="hover:text-primary-600">Blog</Link>
          <span>/</span>
          <span className="text-gray-800 truncate">{post.title}</span>
        </nav>

        {/* Header */}
        <header className="mb-8">
          {post.category && (
            <span className="inline-block text-xs font-semibold text-primary-600 bg-primary-50 px-3 py-1 rounded-full mb-4">
              {post.category}
            </span>
          )}
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight">
            {post.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-4">
            <span className="flex items-center gap-1">
              <User className="w-4 h-4" />
              {post.author_name || 'Silux Mobile Team'}
            </span>
            {post.published_at && (
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {new Date(post.published_at).toLocaleDateString('en-GB', {
                  day: 'numeric', month: 'long', year: 'numeric',
                })}
              </span>
            )}
            <button
              onClick={handleShare}
              className="flex items-center gap-1 text-primary-600 hover:text-primary-700 ml-auto"
            >
              <Share2 className="w-4 h-4" />
              Share
            </button>
          </div>
          {post.excerpt && (
            <p className="text-lg text-gray-600 border-l-4 border-primary-400 pl-4 italic">
              {post.excerpt}
            </p>
          )}
        </header>

        {/* Featured Image */}
        {post.og_image && (
          <div className="mb-8 rounded-2xl overflow-hidden">
            <img src={post.og_image} alt={post.title} className="w-full object-cover max-h-96" />
          </div>
        )}

        {/* Content */}
        <div
          className="prose prose-lg max-w-none prose-headings:font-bold prose-headings:text-gray-900 prose-a:text-primary-600 prose-a:no-underline hover:prose-a:underline prose-img:rounded-xl"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        {/* Tags */}
        {tags.length > 0 && (
          <div className="mt-8 flex flex-wrap gap-2">
            <Tag className="w-4 h-4 text-gray-400 mt-1" />
            {tags.map(tag => (
              <span key={tag} className="text-xs bg-gray-100 text-gray-600 px-3 py-1 rounded-full">
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 p-6 bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl text-white">
          <h3 className="text-xl font-bold mb-2">Ready to get connected?</h3>
          <p className="text-white/80 mb-4">
            Browse our cheap SIM cards, mobile broadband and data SIM deals — no hidden fees.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/cheap-sim-cards"
              className="px-5 py-2 bg-white text-primary-700 rounded-xl font-semibold text-sm hover:bg-gray-50 transition-all"
            >
              Cheap SIM Cards
            </Link>
            <Link
              to="/cheap-broadband"
              className="px-5 py-2 bg-white/20 text-white rounded-xl font-semibold text-sm hover:bg-white/30 transition-all border border-white/30"
            >
              Cheap Broadband
            </Link>
            <Link
              to="/sim-only"
              className="px-5 py-2 bg-white/20 text-white rounded-xl font-semibold text-sm hover:bg-white/30 transition-all border border-white/30"
            >
              Free SIM Card
            </Link>
          </div>
        </div>

        {/* Back to Blog */}
        <div className="mt-8">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-gray-600 hover:text-primary-600 font-medium transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>
        </div>
      </article>

      {/* Related Articles */}
      {relatedPosts.length > 0 && (
        <section className="py-12 px-4 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Related Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedPosts.map((p: any) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  className="bg-white rounded-2xl p-6 border border-gray-100 hover:shadow-md transition-all hover:-translate-y-1"
                >
                  <span className="text-xs text-primary-600 font-semibold bg-primary-50 px-2 py-1 rounded-full">
                    {p.category}
                  </span>
                  <h3 className="font-bold text-gray-900 mt-3 mb-2 line-clamp-2">{p.title}</h3>
                  <p className="text-sm text-gray-600 line-clamp-2">{p.excerpt}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  )
}
